// src/services/userMutes.js
// Faz 1F Görev 4 — Kullanıcı sessize alma (mute) servisi.
//
// Mimari (block ile birebir):
//   users/{muterUid}/muted/{mutedUid}
//     doc: { muterId, mutedId, createdAt }
//
// Block'tan farkları:
//   - Karşı taraf bilmez (profil sayfasında "Engellendi" badge'ı yok).
//   - Takip ilişkisi korunur.
//   - Write-side enforcement YOK — mute sadece okuma-tarafı filtre.
//   - Bildirim de gelmez (Karar 1: mute → bildirim suppress).
//
// Güvenlik: firestore.rules ikinci katman — self-mute engelli, başkasının
// doc'una yazma engelli. Bu servis client-side best-effort doğrular.
//
// P2-6 (PROFIL_DERIN_ANALIZ_V2 §5) — Post/story ayrımı (IG paritesi).
//   doc: { muterId, mutedId, muteType, createdAt }
//   muteType: "all" | "posts" | "stories" — eski kayıtlarda alan yok →
//   "all" varsayılır (geriye dönük uyumlu). Tip değişikliği rules'ta
//   update kapalı olduğu için delete + create ile yapılır.

import { auth, db } from "../firebase";
import { durumCoz } from "./iliskiDurumu";
import type { IliskiDurumu } from "./iliskiDurumu";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  onSnapshot,
  query,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  MUTE_SUBCOLLECTION,
  MUTE_LIST_PAGE_SIZE,
  MUTE_CACHE_TTL_MS,
} from "../constants/userMutes";

/**
 * Sessize-alma kapsamı (P2-6). Legacy kayıtlarda alan YOK → "all" varsayılır,
 * bu yüzden `normalizeMuteType` her girdiyi bu üçlüye indirger.
 */
export type MuteTipi = "all" | "posts" | "stories";

export const MUTE_TYPES: MuteTipi[] = ["all", "posts", "stories"];

/**
 * Mute dinleyicisi. 2. arg `muteType` (mute yoksa `null`), 3. arg EMİR #97/A5
 * üç-değerli durum. Yalnız ilk argümanı okuyan çağıranlar etkilenmez.
 */
export type MuteDinleyicisi = (
  sessize: boolean,
  tip: MuteTipi | null,
  durum: IliskiDurumu,
) => void;

/**
 * Geçersiz/eksik tipleri "all"a indirger (legacy doc + defansif API).
 * Girdi `unknown`: `useModerationActions` yorumuna göre eskiden buraya EVENT
 * NESNESİ bile geçiliyordu ve gövde onu bilerek "all"a düşürüyor.
 */
export function normalizeMuteType(t: unknown): MuteTipi {
  return MUTE_TYPES.includes(t as MuteTipi) ? (t as MuteTipi) : "all";
}

function requireUser() {
  const u = auth.currentUser;
  if (!u) throw new Error("AUTH_REQUIRED");
  return u;
}

function mutedRef(muterUid: string, mutedUid: string) {
  return doc(
    db,
    "users",
    String(muterUid),
    MUTE_SUBCOLLECTION,
    String(mutedUid)
  );
}

/**
 * Hedef kullanıcıyı sessize al. Idempotent.
 */
export async function muteUser(
  // Gövde `if (!targetUid) throw new Error("TARGET_REQUIRED")` ile nullish'i
  // AÇIKÇA karşılıyor; `StoryModal*` `current?.authorId` geçiyor.
  targetUid: string | null | undefined,
  muteType: unknown = "all",
): Promise<true> {
  const me = requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");
  if (me.uid === targetUid) throw new Error("SELF_MUTE_FORBIDDEN");

  const payload = {
    muterId: me.uid,
    mutedId: String(targetUid),
    // P2-6 — post/story ayrımı; geçersiz değer "all"a düşer.
    muteType: normalizeMuteType(muteType),
    createdAt: serverTimestamp(),
  };
  const ref = mutedRef(me.uid, targetUid);
  try {
    await setDoc(ref, payload);
  } catch (e) {
    // Doc zaten varsa rules `update: false` reddeder — tip değişikliği
    // delete + create ile yapılır (idempotent semantik korunur).
    if (String((e as { code?: string } | null | undefined)?.code || "").includes("permission-denied")) {
      await deleteDoc(ref);
      await setDoc(ref, payload);
    } else {
      throw e;
    }
  }

  invalidateMutedUidsCache(me.uid);
  return true;
}

/**
 * Sessizden çıkar.
 */
export async function unmuteUser(targetUid: string): Promise<true> {
  const me = requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");

  try {
    await deleteDoc(mutedRef(me.uid, targetUid));
  } catch {
    // Doc yoksa da sorun değil — idempotent semantik.
  }

  invalidateMutedUidsCache(me.uid);
  return true;
}

/**
 * Hedef kullanıcı sessize alınmış mı? One-shot.
 */
export async function isMuted(targetUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) return false;
  try {
    const snap = await getDoc(mutedRef(me.uid, targetUid));
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * Mute durumunu canlı izle.
 *
 * @returns unsubscribe
 */
export function watchIsMuted(
  targetUid: string,
  cb: MuteDinleyicisi,
): () => void {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) {
    cb(false, null, "hayir");
    return () => {};
  }
  // EMİR #97 / A5 — hata dalı artık "hayır" DEMİYOR. İkinci argüman üç
  // değerli durum (`iliskiDurumu.ts`); yalnız boolean okuyan eski çağıranlar
  // ETKİLENMEZ (watchIsMuted'ın muteType deseniyle aynı).
  const unsub = onSnapshot(
    mutedRef(me.uid, targetUid),
    // P2-6 — ikinci arg muteType; ÜÇÜNCÜ arg #97/A5 durumu.
    (snap) => cb(snap.exists(), snap.exists() ? normalizeMuteType(snap.data()?.muteType) : null, durumCoz(snap.exists())),
    () => cb(false, null, durumCoz(false, true)),
  );
  return unsub;
}

// ── Cache (block ile aynı TTL deseni) ───────────────────────────────
// P2-6 — cache artık uid → muteType map'i tutar; getMutedUids geriye dönük
// uyumlu olarak tüm uid'leri döndürmeye devam eder.
let _cacheUid: string | null = null;
let _cacheMap: Record<string, MuteTipi> = {};
let _cacheExpiresAt = 0;

/**
 * Aktif kullanıcının sessize aldığı uid'leri döndürür. 60 sn cache'li.
 */
export async function getMutedUids(
  viewerId: string | null | undefined,
  opts: { force?: boolean } = {},
): Promise<string[]> {
  const map = await getMutedMap(viewerId, opts);
  return Object.keys(map);
}

/**
 * P2-6 — Aktif kullanıcının mute map'i: { uid: muteType }. 60 sn cache'li.
 * Feed "all"|"posts", story ring "all"|"stories" tiplerini filtreler.
 */
export async function getMutedMap(
  viewerId: string | null | undefined,
  opts: { force?: boolean } = {},
): Promise<Record<string, MuteTipi>> {
  if (!viewerId) return {};
  const { force = false } = opts;
  const now = Date.now();

  if (!force && _cacheUid === viewerId && _cacheExpiresAt > now) {
    return _cacheMap;
  }

  const map: Record<string, MuteTipi> = {};
  try {
    const base = collection(db, `users/${viewerId}/${MUTE_SUBCOLLECTION}`);
    const snap = await getDocs(query(base, limitFn(MUTE_LIST_PAGE_SIZE)));
    snap.forEach((d) => {
      map[String(d.id)] = normalizeMuteType(d.data()?.muteType);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    const hata = e as { code?: string; message?: string } | null | undefined;
    console.warn("[userMutes] getMutedMap okunamadı:", hata?.code || hata?.message);
  }

  _cacheUid = viewerId;
  _cacheMap = map;
  _cacheExpiresAt = Date.now() + MUTE_CACHE_TTL_MS;
  return map;
}

/**
 * Cache invalidation.
 */
export function invalidateMutedUidsCache(viewerId?: string | null): void {
  if (!viewerId || viewerId === _cacheUid) {
    _cacheUid = null;
    _cacheMap = {};
    _cacheExpiresAt = 0;
  }
}

/**
 * Hata kodlarını TR mesajına çevirir.
 */
export function mapMuteError(e: unknown): string {
  const hata = e as { message?: string; code?: string } | null | undefined;
  const code = String(hata?.message || hata?.code || e || "ERR");
  if (code.includes("AUTH_REQUIRED")) return "Giriş yapmalısın.";
  if (code.includes("SELF_MUTE_FORBIDDEN")) return "Kendini sessize alamazsın.";
  if (code.includes("TARGET_REQUIRED")) return "Hedef kullanıcı belirsiz.";
  if (code.includes("permission-denied")) return "İzin reddedildi.";
  return "İşlem başarısız. Lütfen tekrar dene.";
}

const api = {
  muteUser,
  unmuteUser,
  isMuted,
  watchIsMuted,
  getMutedUids,
  getMutedMap,
  invalidateMutedUidsCache,
  mapMuteError,
};

export default api;
